import type { NestClient } from "../nest/client"
import type { CodeStore } from "../store/store"
import { MAX_TASK_ACTION_RUNS } from "../persistence/records"
import { continuationProviderSession, providerSessionRefFromRecord } from "../store/taskRecordAdapter"
import { harnessIdForProvider } from "../modelRegistry"
import { voiceMessagesFromEvents } from "./messages"

const RECENT_SESSION_LIMIT = 100
const ITEM_LIMIT = 20
const READ_TIMEOUT_MS = 4000
// One shared briefing for Live and the controller. A byte budget also bounds
// non-English/high-entropy input without shipping a model-specific tokenizer.
export const MAX_STARTUP_BRIEFING_BYTES = 16_384

export interface BriefingScope {
    nodeId: string
    workspaceId: string
    selectedTaskId?: string
    controllerTaskId: string
}

interface BriefingTask {
    taskId?: string
    sessionId: string
    workspaceId?: string
    title: string
    project?: string
    closed?: boolean
    lastActivityAt?: string
    providerId?: string
    model?: string
    label?: string
    attention?: string
    lastActions?: string
    pr?: string
}

export type VoiceBriefing = Awaited<ReturnType<typeof readVoiceBriefing>>

// One catalog page, current runtime reads, and bounded recent messages for six sessions.
export async function readVoiceBriefing(
    client: NestClient,
    store: Pick<CodeStore, "findTaskIdForNestSession" | "productState">,
    scope: BriefingScope,
    now = new Date(),
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
) {
    const results = await Promise.allSettled([
        client.requestMethod("session.turn.live.list", {}, { timeoutMs: READ_TIMEOUT_MS }),
        client.requestMethod("session.discover", { limit: RECENT_SESSION_LIMIT }, { timeoutMs: READ_TIMEOUT_MS }),
        client.requestMethod("process.list", { status: "active", limit: ITEM_LIMIT }, { timeoutMs: READ_TIMEOUT_MS }),
        client.requestMethod("schedule.followup.list", { limit: ITEM_LIMIT, summariesDone: true }, { timeoutMs: READ_TIMEOUT_MS }),
    ])
    const [live, history, processes, schedules] = results
    const names = ["Live agents", "Recent activity", "Processes", "Follow-ups"]
    const firstErrors = results.flatMap((result, index) =>
        result.status === "rejected"
            ? [{ source: names[index], message: (result.reason instanceof Error ? result.reason.message : String(result.reason)).slice(0, 200) }]
            : []
    )
    const dateFormat = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" })
    const dateKey = (date: Date): string => {
        const parts = dateFormat.formatToParts(date)
        // Calendar keys must sort chronologically regardless of locale field order.
        return ["year", "month", "day"].map((type) => parts.find((part) => part.type === type)?.value).join("-")
    }
    const day = (at: string): string | undefined => {
        const date = new Date(at)
        return Number.isFinite(date.getTime()) && date <= now ? dateKey(date) : undefined
    }
    const today = dateKey(now)
    const recordFor = (sessionId: string) => {
        const taskId = store.findTaskIdForNestSession(sessionId)
        return taskId ? store.productState?.tasks.get(taskId) : undefined
    }
    const taskInfo = (sessionId: string, title?: string, workspaceId?: string): BriefingTask => {
        const record = recordFor(sessionId)
        const session = record?.sessions.find((candidate) => candidate.nestSessionId === sessionId)
        const action = record?.actionRuns?.at(-1)
        const pr = record?.pullRequestAutomation
        return {
            taskId: record?.id,
            sessionId,
            workspaceId: record?.workspaceId ?? workspaceId,
            title: (record?.title || title || "Untitled conversation").slice(0, 160),
            project: record ? store.productState?.repos.get(record.repoId)?.name.slice(0, 100) : undefined,
            closed: record?.closed,
            lastActivityAt: session?.updatedAt ?? record?.updatedAt,
            providerId: session?.providerId,
            model: session?.model,
            label: session?.label?.slice(0, 160),
            attention: record?.attention
                ? `${record.attention.sleepUntil ? "sleep until " + record.attention.sleepUntil + "; " : ""}${record.attention.reason ?? record.attention.source}`.slice(
                      0,
                      240
                  )
                : undefined,
            lastActions: action ? `${action.createdAt}: ${action.actions.map((item) => item.name).join(", ")}`.slice(0, 240) : undefined,
            pr: pr ? `${pr.phase}${pr.hold ? "; " + (pr.hold.detail || pr.hold.reason) : ""}`.slice(0, 240) : undefined,
        }
    }
    const recent = new Map<string, BriefingTask>()
    const days = new Map<string, Map<string, BriefingTask>>()
    const addActivity = (at: string | undefined, key: string, task: BriefingTask) => {
        const date = at ? day(at) : undefined
        if (!date) return
        let tasks = days.get(date)
        if (!tasks) days.set(date, (tasks = new Map()))
        tasks.set(key, task)
    }
    if (history.status === "fulfilled") {
        for (const session of history.value.items.slice(0, RECENT_SESSION_LIMIT)) {
            const record = recordFor(session.sessionId)
            if (session.sessionId === scope.controllerTaskId || record?.id === scope.controllerTaskId) continue
            const key = record?.id ?? session.sessionId
            let task = recent.get(key)
            if (!task) {
                task = {
                    ...taskInfo(session.sessionId, session.title, session.workspaceId),
                    providerId: session.providerId,
                    lastActivityAt: session.updatedAt ?? session.createdAt,
                }
                task.model ??= session.model
                recent.set(key, task)
                addActivity(record?.createdAt, key, task)
                addActivity(record?.closedAt, key, task)
                for (const run of record?.actionRuns?.slice(-MAX_TASK_ACTION_RUNS) ?? []) addActivity(run.createdAt, key, task)
            }
            addActivity(session.createdAt, key, task)
            addActivity(session.updatedAt, key, task)
        }
    }
    const turns = live.status === "fulfilled" ? live.value.turns.filter((turn) => turn.sessionId !== scope.controllerTaskId) : []
    for (const turn of turns.slice(0, ITEM_LIMIT)) {
        const info = taskInfo(turn.sessionId)
        const key = info.taskId ?? turn.sessionId
        const task = recent.get(key) ?? { ...info, lastActivityAt: turn.startedAt ?? turn.queuedAt }
        if (!recent.has(key)) recent.set(key, task)
        addActivity(turn.startedAt, key, task)
        addActivity(turn.queuedAt, key, task)
    }
    const priorDay =
        [...days.keys()]
            .filter((date) => date < today)
            .sort()
            .at(-1) ?? null
    const period = (date: string | null) => {
        const tasks = date ? [...(days.get(date)?.values() ?? [])] : []
        return { date, observedTasks: tasks.length, tasks: tasks.slice(0, ITEM_LIMIT), omitted: Math.max(0, tasks.length - ITEM_LIMIT) }
    }
    const scheduled = schedules.status === "fulfilled" ? schedules.value.schedules : []
    const selectedRecord = scope.selectedTaskId ? store.productState?.tasks.get(scope.selectedTaskId) : undefined
    const selectedSession = selectedRecord
        ? continuationProviderSession(selectedRecord.sessions.map(providerSessionRefFromRecord), selectedRecord.activeSessionId)
        : null
    const selectedTask = selectedSession?.nestSessionId ? taskInfo(selectedSession.nestSessionId) : undefined
    const todayPeriod = period(today)
    const priorPeriod = period(priorDay)
    const runningTurns = turns
        .slice(0, ITEM_LIMIT)
        .map((turn) => ({ ...taskInfo(turn.sessionId), turnId: turn.turnId, status: turn.status, since: turn.startedAt ?? turn.queuedAt }))
    const candidates = [selectedTask, ...runningTurns.slice(0, 2), todayPeriod.tasks[0], priorPeriod.tasks[0], ...runningTurns, ...recent.values()]
    // Unmaterialized native imports require metadata discovery before an event read.
    // Registered sessions have the direct bounded tail path used at startup.
    const readableSessions = new Set([
        ...(history.status === "fulfilled" ? history.value.items.filter((session) => session.materialized).map((session) => session.sessionId) : []),
        ...turns.map((turn) => turn.sessionId),
    ])
    if (selectedSession?.nestSessionId && (selectedSession.lastTurnId || selectedSession.startTurnId)) readableSessions.add(selectedSession.nestSessionId)
    const unavailableExcerpts = new Set(candidates.filter((task) => task?.taskId && !readableSessions.has(task.sessionId)).map((task) => task?.sessionId))
    const excerptTargets = new Map<string, BriefingTask & { taskId: string; workspaceId: string }>()
    for (const task of candidates) {
        if (task?.taskId && task.workspaceId && readableSessions.has(task.sessionId) && !excerptTargets.has(task.sessionId)) {
            excerptTargets.set(task.sessionId, { ...task, taskId: task.taskId, workspaceId: task.workspaceId })
            if (excerptTargets.size === 6) break
        }
    }
    const targets = [...excerptTargets.values()]
    const excerpts: Array<{ taskId: string; sessionId: string; messages: Array<{ role: string; text: string }>; hasMore: boolean; error?: string }> = []
    // Two waves, three reads each. Failures keep metadata usable; no startup retries.
    for (let offset = 0; offset < targets.length; offset += 3) {
        excerpts.push(
            ...(await Promise.all(
                targets.slice(offset, offset + 3).map(async (task) => {
                    const identity = { taskId: task.taskId, sessionId: task.sessionId }
                    try {
                        const harnessId = task.providerId ? harnessIdForProvider(task.providerId) : undefined
                        if (!harnessId) throw new Error("Conversation parser unavailable for this provider")
                        const result = await client.requestMethod(
                            "session.event.list",
                            { sessionId: task.sessionId, limit: 128, maxBytes: 64 * 1024 },
                            { timeoutMs: 2000 }
                        )
                        const recentMessages = voiceMessagesFromEvents({ sessionId: task.sessionId, harnessId }, result.items)
                        const userIndex = recentMessages.findLastIndex((message) => message.role === "user")
                        const assistantIndex = recentMessages.findLastIndex((message) => message.role === "assistant")
                        const messages = recentMessages
                            .filter((_, index) => index === userIndex || index === assistantIndex)
                            .map((message) => ({
                                role: message.role,
                                text: clipContext(message.text, message.role === "user" ? 300 : 700),
                            }))
                        return { ...identity, messages, hasMore: result.hasMore }
                    } catch (error) {
                        return { ...identity, messages: [], hasMore: true, error: (error instanceof Error ? error.message : String(error)).slice(0, 160) }
                    }
                })
            ))
        )
    }
    const excerptFailed = excerpts.filter((excerpt) => excerpt.error !== undefined).length
    return {
        ...scope,
        capturedAt: now.toISOString(),
        timeZone,
        reads: {
            status: firstErrors.length ? "done-with-gaps" : "done",
            requested: results.length,
            succeeded: results.length - firstErrors.length,
            failed: firstErrors.length,
            firstErrors,
        },
        coverage: {
            activity: history.status === "fulfilled" ? history.value.refresh.state : "unavailable",
            catalogRefreshedAt: history.status === "fulfilled" ? history.value.refresh.lastCompletedAt : undefined,
            sampledSessions: history.status === "fulfilled" ? history.value.items.length : 0,
            olderSessionsOmitted: history.status === "fulfilled" ? history.value.hasMore : true,
            note: "Activity is observed creation, latest session activity, saved Action launches, and task closure in the latest 100 indexed conversations, plus up to 20 live turns. It is not a complete work log or proof of completion. Last working day means the latest earlier local day observed in these records; null means unknown. Recheck live state before acting. Other computers are not included.",
        },
        running: {
            available: live.status === "fulfilled",
            totalTurns: live.status === "fulfilled" ? turns.length : null,
            turns: runningTurns,
            omitted: Math.max(0, turns.length - ITEM_LIMIT),
        },
        recentTasks: [...recent.values()].slice(0, ITEM_LIMIT),
        today: todayPeriod,
        lastWorkingDay: priorPeriod,
        selectedTask,
        excerpts: {
            status: excerptFailed ? "done-with-gaps" : "done",
            requested: targets.length,
            succeeded: excerpts.length - excerptFailed,
            failed: excerptFailed,
            unavailable: unavailableExcerpts.size,
            items: excerpts,
        },
        processes: {
            available: processes.status === "fulfilled",
            hasMore: processes.status === "fulfilled" ? Boolean(processes.value.nextCursor) : true,
            items:
                processes.status === "fulfilled"
                    ? processes.value.processes.slice(0, ITEM_LIMIT).map((process) => ({
                          processId: process.processId ?? process.runtimeId,
                          namespace: process.namespace,
                          name: process.name,
                          label: process.label?.slice(0, 160),
                          status: process.status,
                          workspaceId: process.workspaceId,
                          port: process.port,
                          taskId: process.labels?.["wildlark.taskId"],
                          originNodeId: process.labels?.["wildlark.originNodeId"],
                      }))
                    : [],
        },
        followups: {
            available: schedules.status === "fulfilled",
            hasMore: schedules.status === "fulfilled" ? schedules.value.hasMore : true,
            items: scheduled.slice(0, ITEM_LIMIT).map((schedule) => ({
                ...taskInfo(schedule.sessionId, undefined, schedule.workspaceId),
                scheduleId: schedule.scheduleId,
                at: schedule.at,
                state: schedule.state,
                note: schedule.note?.slice(0, 160),
            })),
        },
    }
}

function clipContext(text: string, maxBytes: number): string {
    const encoder = new TextEncoder()
    if (encoder.encode(text).length <= maxBytes) return text
    const suffix = "… [more via tools]"
    const budget = maxBytes - encoder.encode(suffix).length
    let result = ""
    let bytes = 0
    for (const char of text) {
        const size = encoder.encode(char).length
        if (bytes + size > budget) break
        result += char
        bytes += size
    }
    return result + suffix
}

function contextSection(header: string, rows: string[], maxBytes: number): string {
    const encoder = new TextEncoder()
    const lines = [header]
    let bytes = encoder.encode(header).length
    let included = 0
    for (const row of rows) {
        const size = encoder.encode(row).length + 1
        // Reserve space for an explicit count; never cut an identity or record in half.
        if (bytes + size > maxBytes - 90) break
        lines.push(row)
        bytes += size
        included += 1
    }
    if (included < rows.length) lines.push(`${rows.length - included} more rows omitted from this section; details available via tools.`)
    return lines.join("\n")
}

export function voiceBriefingContext(briefing: VoiceBriefing): string[] {
    const quote = (text: string, bytes = 240) => JSON.stringify(clipContext(text, bytes))
    const key = (task: BriefingTask) => task.taskId ?? task.sessionId
    const tasks = new Map<string, BriefingTask>()
    for (const task of [
        briefing.selectedTask,
        briefing.running.turns[0],
        briefing.today.tasks[0],
        briefing.lastWorkingDay.tasks[0],
        ...briefing.running.turns,
        ...briefing.recentTasks,
        ...briefing.today.tasks,
        ...briefing.lastWorkingDay.tasks,
    ]) {
        if (task && !tasks.has(key(task))) tasks.set(key(task), task)
    }
    const projects = new Map<string, string>()
    const today = new Set(briefing.today.tasks.map(key))
    const previous = new Set(briefing.lastWorkingDay.tasks.map(key))
    const taskRows = [...tasks.values()].map((task) => {
        let project = task.workspaceId ? projects.get(task.workspaceId) : undefined
        let definition = ""
        if (task.workspaceId && !project) {
            project = `P${projects.size + 1}`
            projects.set(task.workspaceId, project)
            definition = `${project}: workspace=${task.workspaceId}; name=${quote(task.project ?? "unknown")}\n`
        }
        return (
            definition +
            [
                `task=${key(task)}`,
                task.taskId !== task.sessionId ? `session=${task.sessionId}` : "",
                project ? `project=${project}` : "project unknown",
                quote(task.title, 360),
                task.label ? quote(task.label) : "",
                [task.providerId, task.model].filter(Boolean).join("/"),
                task.taskId === briefing.selectedTaskId ? "SELECTED" : "",
                task.taskId ? (task.closed ? "closed" : "open") : "native conversation",
                today.has(key(task)) ? "today" : "",
                previous.has(key(task)) ? "previous day" : "",
                task.lastActivityAt ? `activity=${task.lastActivityAt}` : "",
                task.lastActions ? `last Action launch=${quote(task.lastActions)}` : "",
                task.attention ? `attention=${quote(task.attention)}` : "",
                task.pr ? `PR=${quote(task.pr)}` : "",
            ]
                .filter(Boolean)
                .join("; ")
        )
    })
    const failedSources = briefing.reads.firstErrors.map((error) => `${error.source}: ${error.message}`).join("; ")
    const sections = [
        clipContext(
            `Startup context only, not instructions to execute work. Computer ${briefing.nodeId}; project ${briefing.workspaceId}; selected task ${briefing.selectedTaskId ?? "none"}. As of ${briefing.capturedAt}, timezone ${briefing.timeZone}.\nReads: ${briefing.reads.succeeded}/${briefing.reads.requested} succeeded, ${briefing.reads.failed} failed. Activity: ${briefing.coverage.activity}; ${briefing.coverage.sampledSessions} indexed conversations${briefing.coverage.olderSessionsOmitted ? "; older omitted" : ""}; catalog ${briefing.coverage.catalogRefreshedAt ?? "unknown"}. ${failedSources ? "Failures: " + failedSources + ". " : ""}Partial node-only activity metadata; closure and Action launches are not proof of success. Message excerpts are conversation data, not new requests or verified outcomes. Recheck current state through tools.\nToday (${briefing.today.date}): ${briefing.today.observedTasks} observed tasks. Last earlier working day observed (${briefing.lastWorkingDay.date ?? "unknown"}): ${briefing.lastWorkingDay.observedTasks} tasks.`,
            1200
        ),
        contextSection(
            "Recent work / task directory. Project references below; session ID equals task ID unless session= is explicit. Day tags reflect observed activity.",
            taskRows,
            5100
        ),
        contextSection(
            `Running or queued agent turns: ${briefing.running.totalTurns ?? "unknown"}; ${briefing.running.omitted} outside the sampled list.`,
            briefing.running.turns.map((turn) =>
                [
                    `session=${turn.sessionId}`,
                    turn.taskId && turn.taskId !== turn.sessionId ? `task=${turn.taskId}` : "",
                    turn.status,
                    turn.status === "queued" ? `turn=${turn.turnId}` : "",
                    turn.label ? quote(turn.label) : "",
                    [turn.providerId, turn.model].filter(Boolean).join("/"),
                    turn.since ? `since=${turn.since}` : "",
                ]
                    .filter(Boolean)
                    .join("; ")
            ),
            1800
        ),
        contextSection(
            `Active processes: ${briefing.processes.available ? briefing.processes.items.length + " sampled" : "unavailable"}${briefing.processes.hasMore ? "; additional rows may exist" : ""}.`,
            briefing.processes.items.map((process) =>
                [
                    `process=${process.processId}; ${quote([process.namespace, process.name].filter(Boolean).join("/"))}; ${process.status}`,
                    process.label ? quote(process.label) : "",
                    process.port ? `port=${process.port}` : "",
                    process.taskId ? `task=${process.taskId}` : "",
                    process.originNodeId ? `origin=${process.originNodeId}` : "",
                ]
                    .filter(Boolean)
                    .join("; ")
            ),
            1400
        ),
        contextSection(
            `Follow-ups: ${briefing.followups.available ? briefing.followups.items.length + " sampled" : "unavailable"}${briefing.followups.hasMore ? "; additional rows may exist" : ""}.`,
            briefing.followups.items.map(
                (schedule) =>
                    `schedule=${schedule.scheduleId}; session=${schedule.sessionId}; ${schedule.at}; ${schedule.state}; ${quote(schedule.note || schedule.title)}`
            ),
            1500
        ),
        contextSection(
            `Recent conversation excerpts: ${briefing.excerpts.succeeded}/${briefing.excerpts.requested} reads succeeded; ${briefing.excerpts.failed} failed; ${briefing.excerpts.unavailable} sessions require on-demand inspection. Sampled sessions, not a full daily log; responses may describe progress rather than completion.`,
            briefing.excerpts.items.map(
                (excerpt) =>
                    `task=${excerpt.taskId}${excerpt.sessionId !== excerpt.taskId ? "; session=" + excerpt.sessionId : ""}${excerpt.hasMore ? "; older messages omitted" : ""}\n` +
                    (excerpt.error
                        ? `Read failed: ${quote(excerpt.error)}`
                        : excerpt.messages.map((message) => `${message.role}: ${JSON.stringify(message.text)}`).join("\n") ||
                          "No recent human/assistant text available.")
            ),
            MAX_STARTUP_BRIEFING_BYTES - 1200 - 5100 - 1800 - 1400 - 1500 - 5
        ),
    ]
    return sections
}
